import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  NominatedPerson,
  CreateNominatedPersonPayload,
  InsurancePolicy,
  CreateInsurancePolicyPayload,
  LegalEntity,
  CreateLegalEntityPayload,
} from '@/lib/types';

const FIVE_MINUTES = 1000 * 60 * 5;

// ---------------------------------------------------------------------------
// Nominated Personnel
// ---------------------------------------------------------------------------

interface NominatedPersonnelFilters {
  role?: string;
  is_active?: string;
}

function cleanFilters(filters: Record<string, string | undefined>): Record<string, string> {
  const cleaned: Record<string, string> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') {
      cleaned[key] = value;
    }
  }
  return cleaned;
}

export function useNominatedPersonnel(filters: NominatedPersonnelFilters = {}) {
  const cleaned = cleanFilters(filters as Record<string, string | undefined>);
  return useQuery({
    queryKey: queryKeys.nominatedPersonnel.list(cleaned),
    queryFn: async (): Promise<NominatedPerson[]> => {
      const response = await apiClient.get<{ results: NominatedPerson[] } | NominatedPerson[]>(
        '/api/v1/compliance/personnel/',
        { params: cleaned }
      );
      const data = response.data;
      if (Array.isArray(data)) return data;
      if (data && Array.isArray((data as { results: NominatedPerson[] }).results)) return (data as { results: NominatedPerson[] }).results;
      return [];
    },
    staleTime: FIVE_MINUTES,
  });
}

export function useCreateNominatedPerson() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateNominatedPersonPayload): Promise<NominatedPerson> => {
      const response = await apiClient.post<NominatedPerson>(
        '/api/v1/compliance/personnel/',
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.nominatedPersonnel.all });
    },
  });
}

export function useUpdateNominatedPerson() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<CreateNominatedPersonPayload>;
    }): Promise<NominatedPerson> => {
      const response = await apiClient.patch<NominatedPerson>(
        `/api/v1/compliance/personnel/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.nominatedPersonnel.all });
    },
  });
}

export function useValidateCert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await apiClient.post(
        `/api/v1/compliance/personnel/${id}/validate-cert/`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.nominatedPersonnel.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Insurance Policies
// ---------------------------------------------------------------------------

interface InsurancePolicyFilters {
  policy_type?: string;
  covers_rope_access?: string;
}

export function useInsurancePolicies(filters: InsurancePolicyFilters = {}) {
  const cleaned = cleanFilters(filters as Record<string, string | undefined>);
  return useQuery({
    queryKey: queryKeys.insurancePolicies.list(cleaned),
    queryFn: async (): Promise<InsurancePolicy[]> => {
      const response = await apiClient.get<{ results: InsurancePolicy[] } | InsurancePolicy[]>(
        '/api/v1/compliance/insurance/',
        { params: cleaned }
      );
      const data = response.data;
      if (Array.isArray(data)) return data;
      if (data && Array.isArray((data as { results: InsurancePolicy[] }).results)) return (data as { results: InsurancePolicy[] }).results;
      return [];
    },
    staleTime: FIVE_MINUTES,
  });
}

export function useCreateInsurancePolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateInsurancePolicyPayload): Promise<InsurancePolicy> => {
      const response = await apiClient.post<InsurancePolicy>(
        '/api/v1/compliance/insurance/',
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.insurancePolicies.all });
    },
  });
}

export function useUpdateInsurancePolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<CreateInsurancePolicyPayload>;
    }): Promise<InsurancePolicy> => {
      const response = await apiClient.patch<InsurancePolicy>(
        `/api/v1/compliance/insurance/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.insurancePolicies.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Legal Entity
// ---------------------------------------------------------------------------

export function useLegalEntities() {
  return useQuery({
    queryKey: queryKeys.legalEntity.list({}),
    queryFn: async (): Promise<LegalEntity[]> => {
      const response = await apiClient.get<{ results: LegalEntity[] } | LegalEntity[]>(
        '/api/v1/compliance/legal-entity/'
      );
      const data = response.data;
      if (Array.isArray(data)) return data;
      if (data && Array.isArray((data as { results: LegalEntity[] }).results)) return (data as { results: LegalEntity[] }).results;
      return [];
    },
    staleTime: FIVE_MINUTES,
  });
}

export function useCreateLegalEntity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateLegalEntityPayload): Promise<LegalEntity> => {
      const response = await apiClient.post<LegalEntity>(
        '/api/v1/compliance/legal-entity/',
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.legalEntity.all });
    },
  });
}

export function useUpdateLegalEntity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<CreateLegalEntityPayload>;
    }): Promise<LegalEntity> => {
      const response = await apiClient.patch<LegalEntity>(
        `/api/v1/compliance/legal-entity/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.legalEntity.all });
    },
  });
}
